import React, { useState } from 'react';
import { ErrorDetailView } from './ErrorDetailView';
import { JsonSyntax } from './JsonSyntax';
import type { DualWriteEvent } from './types';

export function RecentErrors({ events }: { events: DualWriteEvent[] }) {
  const errors = events.filter((e) => e.type === 'error' || e.type === 'warning').slice(0, 5);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  if (errors.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-medium text-content-tertiary mb-3">
        Recent Errors & Warnings
        <span className="ml-2 text-xs font-normal text-icon-subtle">({errors.length})</span>
      </h3>
      <div className="rounded-lg border border-border-secondary overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-bg-secondary text-left text-icon-subtle uppercase sticky top-0">
            <tr>
              <th className="px-3 py-1.5">Time</th>
              <th className="px-3 py-1.5">Type</th>
              <th className="px-3 py-1.5">Shape</th>
              <th className="px-3 py-1.5">Kind</th>
              <th className="px-3 py-1.5">Legacy ID</th>
              <th className="px-3 py-1.5">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-tertiary">
            {errors.map((evt) => {
              const eventKey = `${evt.timestamp}-${evt.legacyId}`;
              const isExpanded = expandedKey === eventKey;
              return (
                <React.Fragment key={eventKey}>
                  <tr
                    className={`${evt.type === 'error' ? 'bg-bg-red-light' : 'bg-tangerine-50'} cursor-pointer`}
                    onClick={() => setExpandedKey(isExpanded ? null : eventKey)}
                  >
                    <td className="px-3 py-1.5 font-mono text-icon-subtle">
                      {new Date(evt.timestamp).toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                        evt.type === 'error' ? 'bg-bg-red-light text-content-danger' : 'bg-tangerine-50 text-tangerine-600'
                      }`}>
                        {evt.type}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-content-tertiary">{evt.shape}</td>
                    <td className="px-3 py-1.5">{evt.blueprintKind ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-content-tertiary">{evt.legacyId}</td>
                    <td className="px-3 py-1.5 text-icon-subtle">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate max-w-[200px]">{evt.error}</span>
                        <span className="text-icon-subtle">{isExpanded ? '▾' : '▸'}</span>
                        <button
                          title="Copy as JSON"
                          className="shrink-0 text-icon-subtle hover:text-content-tertiary transition-colors"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            void navigator.clipboard.writeText(JSON.stringify({
                              error: evt.error, errorCode: evt.errorCode, errorDetails: evt.errorDetails,
                              shape: evt.shape, blueprintKind: evt.blueprintKind, legacyId: evt.legacyId,
                              externalId: evt.externalId, payload: evt.payload, timestamp: evt.timestamp,
                            }, null, 2));
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                            <path d="M5.5 3.5A1.5 1.5 0 0 1 7 2h2.879a1.5 1.5 0 0 1 1.06.44l2.122 2.12a1.5 1.5 0 0 1 .439 1.061V9.5A1.5 1.5 0 0 1 12 11V8.621a3 3 0 0 0-.879-2.121L9 4.379A3 3 0 0 0 6.879 3.5H5.5Z" />
                            <path d="M4 5a1.5 1.5 0 0 0-1.5 1.5v6A1.5 1.5 0 0 0 4 14h5a1.5 1.5 0 0 0 1.5-1.5V8.621a1.5 1.5 0 0 0-.44-1.06L7.94 5.439A1.5 1.5 0 0 0 6.878 5H4Z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className={`${evt.type === 'error' ? 'bg-bg-red-light/50' : 'bg-tangerine-50/50'}`}>
                      <td colSpan={6} className="px-3 py-3">
                        <div className="space-y-2">
                          {evt.errorCode && (
                            <div className="text-xs">
                              <span className={`font-medium ${evt.type === 'error' ? 'text-content-danger' : 'text-tangerine-600'}`}>
                                {evt.type === 'error' ? 'Rejection' : 'Warning'}:{' '}
                              </span>
                              <span className={`font-mono ${evt.type === 'error' ? 'text-content-danger' : 'text-tangerine-500'}`}>{evt.errorCode}</span>
                              <span className="text-content-tertiary"> — {evt.error}</span>
                            </div>
                          )}
                          {evt.errorDetails && Object.keys(evt.errorDetails).length > 0 && (
                            <div>
                              <div className="text-xs font-medium text-content-tertiary mb-1">Details</div>
                              <ErrorDetailView errorCode={evt.errorCode} details={evt.errorDetails} />
                            </div>
                          )}
                          {evt.payload && (
                            <div>
                              <div className="text-xs font-medium text-content-tertiary mb-1">Payload</div>
                              <JsonSyntax data={evt.payload} />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
